/* eslint-disable prettier/prettier */
import { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Accordion, AccordionTab } from "primereact/accordion";
import AdminServices from "services/admin-services";
import InvestorService from "services/invoice-service";
import { setToaster } from "common/toastStore";
import { Sidebar } from "primereact/sidebar";
import PackageConfigs from "./AddPackageConfiguration";
import DeliveryConfigForm from "./deliveryConfigForm";
import EncompassConfigForm from "./encompassForm";

interface IDeliveryConfiguration {
  _rowId: string;
  deliveryType?: number;
  url?: string;
  port?: number;
  userName?: string;
  password?: string;
  privateKeyFile?: string;
  passPhrase?: string;
  path?: string;
  subFolderName?: string;
}

interface IApiPackage {
  id: number;
  investorPackageId: number;
  packageName: string;
  package?: {
    id?: number;
    name?: string;
  };
  packageId?: number;
  isActive?: boolean;
  bundleConfiguration?: string;
  deliveryConfiguraiton?: string;
  encompassUpdateConfiguration?: string;
}

interface IViewPackageRow {
  id: number;
  packageId: number;
  investorPackageId?: number;
  packageMasterId?: number;
  packageName: string;
  contentType: any;
  mergeOption: any;
  outputFileName: string;
  folderName: string;
  zipPackage: boolean;
  zipFileName: string;
  isActive: boolean;
  deliveryOptions: IDeliveryConfiguration[];
  encompassUpdateConfiguration?: encompassUpdateConfiguration[];
}

interface encompassUpdateConfiguration {
  _rowId: string;
  name: string;
  value: string;
}
[];

const parseJson = (value: string | undefined, fallback: any) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

const ViewPackages = ({
  data,
  packageList,
  selectedInvestor,
  refreshPackages,
  basicConfiguration,
}: any) => {
  const [mergeOptions, setMergeOptions] = useState<any[]>([]);
  const [contentTypes, setContentTypes] = useState<any[]>([]);
  const [packageRows, setPackageRows] = useState<IViewPackageRow[]>([]);

  const [showEncompassSidebar, setShowEncompassSidebar] = useState(false);
  const [selectedEncompass, setSelectedEncompass] = useState<any>(null);
  const [selectedPackageForEncompass, setSelectedPackageForEncompass] =
    useState<IViewPackageRow | null>(null);
  const [encompassMode, setEncompassMode] = useState<"create" | "edit">(
    "create",
  );

  const [showDeliverySidebar, setShowDeliverySidebar] = useState(false);
  const [selectedDelivery, setSelectedDelivery] =
    useState<IDeliveryConfiguration | null>(null);
  const [selectedPackageForDelivery, setSelectedPackageForDelivery] =
    useState<IViewPackageRow | null>(null);
  const [deliveryMode, setDeliveryMode] = useState<"create" | "edit">("create");

  const [showSidebar, setShowSidebar] = useState(false);
  const [selectedPackage, setSelectedPackage] =
    useState<IViewPackageRow | null>(null);
  const [mode, setMode] = useState<"view" | "edit" | "create">("view");
  const [activePackageIndex, setActivePackageIndex] = useState<number | null>(0);

  useEffect(() => {
    AdminServices.getPackageMergeTypes().then((r: any) =>
      setMergeOptions(r?.data || []),
    );
    AdminServices.getPackageContentTypes().then((r: any) =>
      setContentTypes(r?.data || []),
    );
  }, []);

  useEffect(() => {
    const source = Array.isArray(data) ? data : data ? [data] : [];

    const rows = source.map((item: IApiPackage) => {
      const matchedPackage = (packageList || []).find(
        (pkg: any) =>
          String(pkg?.investorPackageId ?? pkg?.packageData?.id) ===
          String(item.investorPackageId),
      );
      const bundle = parseJson(item.bundleConfiguration, {});
      const delivery = parseJson(item.deliveryConfiguraiton, []);
      const encompass = parseJson(item.encompassUpdateConfiguration, []);
      const deliveryList = Array.isArray(delivery)
        ? delivery
        : delivery
          ? [delivery]
          : [];
      const encompassList = Array.isArray(encompass)
        ? encompass
        : encompass
          ? [encompass]
          : [];

      return {
        id: item.id,
        packageId: Number(item.investorPackageId),
        packageMasterId:
          item?.package?.id ?? item?.packageId ?? matchedPackage?.value,
        packageName: item.packageName,
        investorPackageId: item.investorPackageId,
        contentType: bundle.contentType,
        mergeOption: bundle.mergeOption,
        outputFileName: bundle.outputFileName || "",
        folderName: bundle.folderName || "",
        zipPackage: Boolean(bundle.zipPackage),
        zipFileName: bundle.zipFileName || "",
        isActive: item.isActive ?? true,
        encompassUpdateConfiguration: encompassList.map(
          (e: any, i: number) => ({
            ...e,
            _rowId: `${item.id}-enc-${i}`,
          }),
        ),
        deliveryOptions: deliveryList.map((d: any, i: number) => ({
          ...d,
          _rowId: `${item.id}-${i}`,
        })),
      };
    });

    setPackageRows(rows);
  }, [data, packageList]);

  const updatePackage = (pkg: IViewPackageRow) => {
    const payload = {
      id: pkg.id,
      isActive: pkg.isActive,
      investorPackageId: pkg.packageId,
      packageName: pkg.packageName,
      bundleConfiguration: JSON.stringify({
        contentType: pkg.contentType,
        mergeOption: pkg.mergeOption,
        outputFileName: pkg.outputFileName,
        folderName: pkg.folderName,
        zipPackage: pkg.zipPackage,
        zipFileName: pkg.zipFileName,
      }),
      deliveryConfiguraiton: JSON.stringify(pkg.deliveryOptions),
      encompassUpdateConfiguration: JSON.stringify(
        pkg.encompassUpdateConfiguration || [],
      ),
    };

    return InvestorService.updateInvestorsPackage(payload)
      .then(() => {
        setToaster({
          severity: "success",
          summary: "Success",
          detail: "Updated successfully",
        });
        refreshPackages();
      })
      .catch((err: any) => {
        setToaster({
          severity: "error",
          summary: "Error",
          detail: err?.response?.data || "Update failed",
        });

        throw err;
      });
  };

  const getContentTypeName = (contentType: any) => {
    const type = contentTypes.find(
      (item) => String(item.id) === String(contentType),
    );

    return type?.name || "-";
  };

  const getMergeOptionName = (mergeOption: any) => {
    const option = mergeOptions.find(
      (item) => String(item.id) === String(mergeOption),
    );

    return option?.name || "-";
  };

  const getDeliveryTypeLabel = (deliveryType?: number) => {
    return deliveryType === 1 ? "Folder Copy" : "SFTP";
  };

  const removeDeliveryConfiguration = (
    pkg: IViewPackageRow,
    rowId: string,
  ) => {
    if (!window.confirm("Delete this delivery configuration?")) return;

    const updatedPkg = {
      ...pkg,
      deliveryOptions: pkg.deliveryOptions.filter((item) => item._rowId !== rowId),
    };

    setPackageRows((prev) =>
      prev.map((item) => (item.id === pkg.id ? updatedPkg : item)),
    );
    updatePackage(updatedPkg);
  };

  return (
    <>
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-xl font-bold">Packages</h2>
        <Button
          label="Add Package Configuration"
          icon="pi pi-plus"
          onClick={() => {
            setShowSidebar(true);
            setMode("create");
          }}
          className="p-button-success p-button-outlined p-button-sm"
        />
      </div>

      <div>
        {packageRows.length > 0 ? (
          <Accordion
            activeIndex={activePackageIndex}
            onTabChange={(e) => setActivePackageIndex(e.index as number)}
          >
            {packageRows.map((pkg) => (
              <AccordionTab
                key={pkg.id}
                header={
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{pkg.packageName}</span>
                    <span className="text-sm text-gray-500">
                      {getContentTypeName(pkg.contentType)}
                    </span>
                  </div>
                }
              >
                <div className="rounded overflow-hidden border border-gray-200">
                  <div className="flex items-start justify-between gap-4 border-b bg-white px-4 py-3">
                    <div className="flex min-w-0 items-start gap-3">
                      <div className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded border border-gray-300 bg-gray-50 text-gray-600">
                        <i className="pi pi-file" />
                      </div>

                      <div className="min-w-0">
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-600">
                          <span>{getContentTypeName(pkg.contentType)}</span>
                          <span>|</span>
                          <span className="truncate">
                            {pkg.outputFileName || "No output file"}
                          </span>
                        </div>

                        <div className="mt-2 flex flex-wrap gap-2 text-xs">
                          <span
                            className={`rounded px-2 py-1 font-medium ${
                              pkg.isActive
                                ? "bg-green-100 text-green-700"
                                : "bg-red-100 text-red-700"
                            }`}
                          >
                            Status: {pkg.isActive ? "Active" : "Inactive"}
                          </span>
                          <span className="rounded bg-blue-50 px-2 py-1 font-medium text-blue-700">
                            Merge: {getMergeOptionName(pkg.mergeOption)}
                          </span>
                          <span
                            className={`rounded px-2 py-1 font-medium ${
                              pkg.folderName
                                ? "bg-indigo-50 text-indigo-700"
                                : "bg-gray-100 text-gray-400"
                            }`}
                          >
                            Folder: {pkg.folderName || "-"}
                          </span>
                          <span
                            className={`rounded px-2 py-1 font-medium ${
                              pkg.zipPackage
                                ? "bg-green-50 text-green-700"
                                : "bg-gray-100 text-gray-400"
                            }`}
                          >
                            Zip: {pkg.zipPackage ? "Yes" : "No"}
                          </span>
                          <span
                            className={`rounded px-2 py-1 font-medium ${
                              pkg.zipFileName
                                ? "bg-indigo-50 text-indigo-700"
                                : "bg-gray-100 text-gray-400"
                            }`}
                          >
                            Zip File: {pkg.zipFileName || "-"}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-3 text-sm text-gray-700">
                      <Button
                        label="Edit"
                        icon="pi pi-pencil"
                        className="p-button-outlined p-button-sm"
                        onClick={() => {
                          setSelectedPackage(pkg);
                          setMode("edit");
                          setShowSidebar(true);
                        }}
                      />
                    </div>
                  </div>

                  {/* DELIVERY TABLE */}
                  {/* {pkg.deliveryOptions?.length > 0 && ( */}
                  <div className="mt-3 bg-gray-50 p-3 rounded">
                    <div className="flex justify-between mb-2">
                      <b>Delivery Configurations</b>

                      <Button
                        icon="pi pi-plus"
                        label="Add New"
                        className="p-button-success p-button-outlined p-button-sm"
                        onClick={() => {
                          setSelectedPackageForDelivery(pkg);
                          setSelectedDelivery(null);
                          setDeliveryMode("create");
                          setShowDeliverySidebar(true);
                        }}
                      />
                    </div>

                    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                      {pkg.deliveryOptions?.length > 0 ? (
                        pkg.deliveryOptions.map((row) => (
                          <div
                            key={row._rowId}
                            className="rounded border border-gray-200 bg-white p-3"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex min-w-0 items-start gap-3">
                                <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded border border-gray-300 bg-gray-50 text-gray-600">
                                  <i
                                    className={`pi ${
                                      row.deliveryType === 1 ? "pi-folder" : "pi-lock"
                                    }`}
                                  />
                                </div>

                                <div className="min-w-0">
                                  <div className="text-lg font-semibold text-gray-900">
                                    {getDeliveryTypeLabel(row.deliveryType)}
                                  </div>
                                </div>
                              </div>

                              <div className="flex shrink-0 gap-2">
                                <Button
                                  label="Edit"
                                  icon="pi pi-pencil"
                                  className="p-button-outlined p-button-sm"
                                  onClick={() => {
                                    setSelectedPackageForDelivery(pkg);
                                    setSelectedDelivery(row);
                                    setDeliveryMode("edit");
                                    setShowDeliverySidebar(true);
                                  }}
                                />
                                <Button
                                  label="Delete"
                                  icon="pi pi-trash"
                                  className="p-button-outlined p-button-danger p-button-sm"
                                  onClick={() =>
                                    removeDeliveryConfiguration(pkg, row._rowId)
                                  }
                                />
                              </div>
                            </div>

                            {row.deliveryType === 1 ? (
                              <div className="mt-3 space-y-2 text-sm text-gray-600">
                                <div className="flex items-center gap-2">
                                  <span className="h-2 w-2 rounded-full bg-gray-400" />
                                  <span className="break-all">{row.path || "-"}</span>
                                </div>
                                {row.subFolderName && (
                                  <div className="ml-4">
                                    <span className="font-medium text-gray-700">Subfolder:</span>{" "}
                                    {row.subFolderName}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="mt-3 grid gap-2 text-sm text-gray-600">
                                <div>
                                  <span className="font-medium text-gray-700">URL</span>{" "}
                                  {row.url || "-"}
                                  {row.port ? `:${row.port}` : ""}
                                </div>
                                <div>
                                  <span className="font-medium text-gray-700">User</span>{" "}
                                  {row.userName || "-"}
                                </div>
                                <div>
                                  <span className="font-medium text-gray-700">Sub Folder</span>{" "}
                                  {row.subFolderName || "-"}
                                </div>
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="rounded border border-dashed border-gray-300 bg-white px-4 py-6 text-sm text-gray-500 md:col-span-2 xl:col-span-3">
                          No delivery configurations added.
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="my-5 bg-gray-50 p-3 rounded">
                    <div className="flex justify-between mb-2">
                      <b>Encompass Update Configuration.</b>

                      <Button
                        icon="pi pi-plus"
                        label="Add New"
                        className="p-button-success p-button-outlined p-button-sm"
                        onClick={() => {
                          setSelectedPackageForEncompass(pkg);
                          setSelectedEncompass(null);
                          setEncompassMode("create");
                          setShowEncompassSidebar(true);
                        }}
                      />
                    </div>

                    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                      {(pkg?.encompassUpdateConfiguration || []).length > 0 ? (
                        (pkg?.encompassUpdateConfiguration || []).map((row: any) => (
                          <div
                            key={row._rowId}
                            className="flex items-start justify-between gap-4 rounded border border-gray-200 bg-white px-3 py-3"
                          >
                            <div className="min-w-0">
                              <div className="text-base font-semibold text-gray-900">
                                {row.name || "-"}
                              </div>
                              <div className="mt-1 text-sm text-gray-600 break-all">
                                {row.value || "-"}
                              </div>
                            </div>

                            <div className="shrink-0">
                              <Button
                                label="Edit"
                                icon="pi pi-pencil"
                                className="p-button-outlined p-button-sm"
                                onClick={() => {
                                  setSelectedPackageForEncompass(pkg);
                                  setSelectedEncompass(row);
                                  setEncompassMode("edit");
                                  setShowEncompassSidebar(true);
                                }}
                              />
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="rounded border border-dashed border-gray-300 bg-white px-4 py-6 text-sm text-gray-500 md:col-span-2 xl:col-span-3">
                          No encompass update configurations added.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </AccordionTab>
            ))}
          </Accordion>
        ) : (
          <div className="rounded border border-dashed border-gray-300 bg-white px-4 py-6 text-sm text-gray-500">
            No packages available.
          </div>
        )}
      </div>

      <Sidebar
        visible={showSidebar}
        onHide={() => {
          setShowSidebar(false);
          setSelectedPackage(null);
        }}
        position="right"
        style={{ width: "60vw" }}
      >
        <PackageConfigs
          mode={mode}
          data={selectedPackage}
          packageList={packageList}
          contentTypes={contentTypes}
          mergeOptions={mergeOptions}
          selectedInvestor={selectedInvestor}
          basicConfiguration={basicConfiguration}
          onHide={() => setShowSidebar(false)}
          onSuccess={() => {
            setShowSidebar(false);
            refreshPackages();
          }}
        />
      </Sidebar>

      <Sidebar
        visible={showDeliverySidebar}
        onHide={() => setShowDeliverySidebar(false)}
        position="right"
        style={{ width: "60vw" }}
      >
        <DeliveryConfigForm
          mode={deliveryMode}
          data={selectedDelivery}
          packageData={selectedPackageForDelivery}
          basicConfiguration={basicConfiguration}
          selectedInvestor={selectedInvestor}
          onCancel={() => setShowDeliverySidebar(false)}
          onSave={async (updatedDeliveries: any) => {
            const updatedPackages = packageRows.map((p) => {
              if (p.id !== selectedPackageForDelivery?.id) return p;

              const updatedPkg = {
                ...p,
                deliveryOptions: updatedDeliveries,
              };

              return updatedPkg;
            });

            const targetPackage = updatedPackages.find(
              (p) => p.id === selectedPackageForDelivery?.id,
            );

            if (!targetPackage) return;

            await updatePackage(targetPackage);

            setPackageRows(updatedPackages);
            setShowDeliverySidebar(false);
          }}
        />
      </Sidebar>

      <Sidebar
        visible={showEncompassSidebar}
        onHide={() => setShowEncompassSidebar(false)}
        position="right"
        style={{ width: "30vw" }}
      >
        <EncompassConfigForm
          mode={encompassMode}
          data={selectedEncompass}
          packageData={selectedPackageForEncompass}
          onCancel={() => setShowEncompassSidebar(false)}
          onSave={async (updated: any) => {
            const updatedPackages = packageRows.map((p) => {
              if (p.id !== selectedPackageForEncompass?.id) return p;

              const updatedPkg = {
                ...p,
                encompassUpdateConfiguration: updated,
              };

              return updatedPkg;
            });

            const targetPackage = updatedPackages.find(
              (p) => p.id === selectedPackageForEncompass?.id,
            );

            if (!targetPackage) return;

            await updatePackage(targetPackage);

            setPackageRows(updatedPackages);
            setShowEncompassSidebar(false);
          }}
        />
      </Sidebar>
    </>
  );
};

export default ViewPackages;
